import React, { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRightOutlined,
  ExpandLess,
  ExpandMore,
  HomeOutlined,
  Inventory2Outlined,
  CoPresentOutlined,
  SafetyDividerOutlined,
  MonetizationOnOutlined,
  AdminPanelSettingsOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import FlexBetween from "./FlexBetween";
import logoImage from "./../assets/logo-prima.png";

// Nav item structure
const navItems = [
  {
    text: "Dashboard",
    path: "dashboard",
    permission: "dashboard",
    icon: <HomeOutlined />,
  },
  {
    text: "Data Master",
    icon: <SafetyDividerOutlined />,
    children: [
      { text: "Departemen", path: "departemen", permission: "departemen" },
      { text: "Divisi", path: "divisi", permission: "divisi" },
      { text: "Jabatan", path: "jabatan", permission: "jabatan" },
      { text: "Karyawan", path: "karyawanbaru", permission: "karyawan" },
      { text: "Notifikasi", path: "notifikasi", permission: "notifikasi" },
      { text: "Panduan", path: "panduan", permission: "panduan" },
      { text: "Aspirasi", path: "aspirasi", permission: "aspirasi" },
      { text: "Peringatan", path: "peringatan", permission: "peringatan" },
      { text: "Jenjang", path: "jenjang", permission: "jenjang" },
    ],
  },
  {
    text: "Stockopname",
    icon: <Inventory2Outlined />,
    children: [
      {
        text: "Data Stockopname",
        path: "stockopname",
        permission: "stockopname",
      },
      {
        text: "Peserta Stockopname",
        path: "pesertaso",
        permission: "pesertaso",
      },
    ],
  },
  {
    text: "Absensi",
    icon: <CoPresentOutlined />,
    children: [
      {
        text: "Absensi Hari Ini",
        path: "absensihariini",
        permission: "absensihariini",
      },
      {
        text: "Rekap Absen Harian",
        path: "rekapabsensiharian",
        permission: "rekapabsensiharian",
      },
      {
        text: "Rekap Absen Bulanan",
        path: "rekapabsensibulanan",
        permission: "rekapabsensibulanan",
      },
    ],
  },
  {
    text: "Keuangan",
    icon: <MonetizationOnOutlined />,
    children: [
      { text: "Tabungan", path: "tabungan", permission: "tabungan" },
      {
        text: "Rekap Tabungan",
        path: "rekaptabungan",
        permission: "rekaptabungan",
      },
      { text: "Gaji Karyawan", path: "penggajian", permission: "penggajian" },
      { text: "Setoran Toko", path: "setoran", permission: "setoran" },
      { text: "Alokasi DC", path: "alokasidc", permission: "alokasidc" },
      { text: "Buku Warkat", path: "bukuwarkat", permission: "bukuwarkat" },
    ],
  },
  {
    text: "Role Management",
    path: "rolemanagement",
    permission: "role_management",
    icon: <AdminPanelSettingsOutlined />,
  },
];

const Sidebar = ({
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();
  const { websiteUser } = useSelector((state) => state.user);

  const hasAccess = (permission) => {
    if (!permission) return true;
    return Boolean(websiteUser?.permissions?.includes(permission));
  };

  const visibleNavItems = navItems
    .map((item) => {
      if (item.children) {
        const children = item.children.filter((child) =>
          hasAccess(child.permission),
        );
        return { ...item, children };
      }
      return item;
    })
    .filter((item) => {
      if (item.children) return item.children.length > 0;
      return hasAccess(item.permission);
    });

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  const toggleSubmenu = (text) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [text]: !prev[text],
    }));
  };

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
              borderWidth: isNonMobile ? 0 : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box m="1.5rem 2rem 2rem 3rem">
              <FlexBetween color={theme.palette.secondary.main}>
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <img
                    src={logoImage}
                    alt="Logo"
                    style={{ width: "120px", height: "auto" }}
                  />
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>

            <List>
              {visibleNavItems.map((item, index) => {
                const isItemActive = active === item.path;

                if (item.children) {
                  const isOpen = openSubmenus[item.text] || false;
                  return (
                    <Box key={index}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => toggleSubmenu(item.text)}
                        >
                          <ListItemIcon
                            sx={{
                              ml: "2rem",
                              color: theme.palette.secondary[200],
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.text} />
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.children.map((child) => {
                            const isChildActive = active === child.path;
                            return (
                              <ListItem key={child.path} disablePadding>
                                <ListItemButton
                                  sx={{
                                    pl: 6,
                                    backgroundColor: isChildActive
                                      ? theme.palette.secondary[900]
                                      : "transparent",
                                    color: isChildActive
                                      ? theme.palette.primary[600]
                                      : theme.palette.secondary[100],
                                  }}
                                  onClick={() => {
                                    navigate(`/${child.path}`);
                                    setActive(child.path);
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      color: isChildActive
                                        ? theme.palette.primary[600]
                                        : theme.palette.secondary[200],
                                    }}
                                  >
                                    {child.icon || <ChevronRightOutlined />}
                                  </ListItemIcon>
                                  <ListItemText primary={child.text} />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    </Box>
                  );
                }

                // Single menu item
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        navigate(`/${item.path}`);
                        setActive(item.path);
                      }}
                      sx={{
                        backgroundColor: isItemActive
                          ? theme.palette.secondary[900]
                          : "transparent",
                        color: isItemActive
                          ? theme.palette.primary[600]
                          : theme.palette.secondary[100],
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "2rem",
                          color: isItemActive
                            ? theme.palette.primary[600]
                            : theme.palette.secondary[200],
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                      {isItemActive && (
                        <ChevronRightOutlined sx={{ ml: "auto" }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
